import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Button, ModalBody, ModalFooter, Text } from "@chakra-ui/react";
import { useToast } from "@/components/ui/Toast";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import useStore from "@/stores/index";
import { getClassroomStore } from "@/stores/classroomStore";
import { ClassCombinationResponse } from "@/js/api/combineClassesApi";
import { ClasswizeModal } from "@/components/ui/Modal";

type props = {
	open: boolean;
	onClose: () => void;
	classroom: ClassroomOverviewDTO;
	classCombination: ClassCombinationResponse[];
	isSaving: boolean;
	setIsSaving: (isSaving: boolean) => void;
};

export const SplitModal: FC<props> = ({ open, onClose, classroom, isSaving, setIsSaving }) => {
	const { t } = useTranslation();
	const { successToast, errorToast } = useToast();

	const title = useMemo(() => classroom.personalisation?.displayName ?? "", [classroom]);

	const handleSplit = async () => {
		const { handleDeleteClassroom, fetchClassrooms } = getClassroomStore(useStore.getState());

		try {
			setIsSaving(true);

			await handleDeleteClassroom(classroom.stableIdV1);
			await fetchClassrooms();
			successToast({
				title: t("overview.splitSuccessTitle"),
				description: t("overview.splitSuccessDescription"),
				isClosable: true,
			});
		} catch (e: unknown) {
			console.error(e);
			errorToast({
				title: `${t("overview.splitErrorPrefix")} ${title}`,
				description: t("overview.splitErrorDescription"),
				isClosable: true,
			});
		} finally {
			setIsSaving(false);
			onClose();
		}
	};

	return (
		<ClasswizeModal title={t("overview.splitTitle")} size="lg" isOpen={open} onClose={onClose}>
			<ModalBody>
				<Text color="#051839" fontSize="16px" lineHeight="20px" data-testid="body-text">
					{t("overview.splitBodyOne")} <b>{title}</b> {t("overview.splitBodyTwo")}
				</Text>
				<br />
				<Text color="#051839" fontSize="16px" lineHeight="20px">
					{t("overview.splitDesc")}
				</Text>
			</ModalBody>

			<ModalFooter>
				<Flex direction="row-reverse">
					<Button onClick={handleSplit} borderRadius="md" data-testid="modal-split-btn" isLoading={isSaving}>
						{t("overview.settingsSplitClasses")}
					</Button>
				</Flex>
			</ModalFooter>
		</ClasswizeModal>
	);
};
